import { Box, Button, CircularProgress, Grid, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import usePlanStore from '../../../../store/planStore';
import BackToList from '../Plan/BackToList/BackToList';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '../Plan/PlanSummary/styles';
import DigitalBudgetTable from '../common/BudgetTable/DigitalBudgetTable/DigitalBudgetTable';
import { groupCalculatedBudgetTouchpointData } from '../common/BudgetTable/utils/groupTouchpointData';
import OfflineBudgetTable from '../common/BudgetTable/OfflineBudgetTable/OfflineBudgetTable';
import { AccordionSummaryContent } from './AccordionSummaryContent/AccordionSummaryContent';
import { TotalBudgetSummary } from './TotalBudgetSummary/TotalBudgetSummary';
import LockedPlanSummaryHeader from './LockedPlanSummaryHeader/LockedPlanSummaryHeader';
import PlanSummaryGeneralInfo from '../Plan/PlanSummary/PlanSummaryGeneralInfo/PlanSummaryGeneralInfo';
import CountryInfoTable from './CountryInfoTable/CountryInfoTable';
import useViewSingleMediaPlan from '../hooks/useViewSingleMediaPlan';
import useGenerateXls from '../Plan/PlanSummary/hooks/useGenerateXls';

import AcceptPlanFlow from '../Plan/PlanSummary/AcceptPlanFlow/AcceptPlanFlow';
import statuses from '../MediaPlansTable/helpers/statuses';
import useAcceptUnlockMediaPlan from '../Plan/PlanSummary/hooks/useAcceptUnlockMediaPlan';
import DDXAcquisitionTable from './DDXAcquisitionTable/DDXAcquisitionTable';
import { LoaderWrapper } from '../../../../shared/styles';
import getAbilityForAccepting from '../Plan/PlanSummary/helpers/getAbilityForAccepting';
import { useAuthContext } from '../../../../context/AuthContext';
import BackToMediaPlanningGuard from '../../../../router/routes/BackToMediaPlanningGuard';

type PendingUnlock = {
  id: string;
  status: string;
  action: 'revert';
} | null;

export default function AcceptedPlanPage() {
  const { storedCountryForms, countryForms, savedGeneralInfoState } =
    usePlanStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    fetchMediaPlan,
    data: mediaPlan,
    isLoading,
  } = useViewSingleMediaPlan();
  const { id } = useParams();
  const { generateXls } = useGenerateXls();
  const { userRoles } = useAuthContext();

  const [pendingUnlock, setPendingUnlock] = useState<PendingUnlock>(null);
  const acceptPlanOpen = Boolean(pendingUnlock);
  const currentStatus = pendingUnlock?.status ?? '';

  const handleCloseAcceptPlan = () => setPendingUnlock(null);

  const { asyncAcceptUnlockMediaPlanTrigger, isLoading: isUnlocking } =
    useAcceptUnlockMediaPlan();

  const handleClickUnlockPlan = () => {
    if (!id) return;

    setPendingUnlock({
      id,
      status: statuses.ACCEPTED,
      action: 'revert',
    });
  };

  const handleProceedAcceptUnlock = () => {
    if (!pendingUnlock?.id) return;

    const planId = pendingUnlock.id;

    asyncAcceptUnlockMediaPlanTrigger(
      { id: planId, action: pendingUnlock.action },
      {
        onSuccess: () => {
          setPendingUnlock(null);
          fetchMediaPlan(planId);
          queryClient.invalidateQueries('get-media-plans');
        },
      }
    );

    navigate(`/media-planning/edit/${id}`, { replace: true });
  };

  useEffect(() => {
    if (!id) return;
    fetchMediaPlan(id);
  }, [id, fetchMediaPlan]);

  const countries = useMemo(
    () => Object.keys(storedCountryForms ?? {}),
    [storedCountryForms]
  );

  return (
    <>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          background: '#fafafa',
          minHeight: 'calc(100vh - 75px)',
          px: 3,
          mb: 6,
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: 2,
              py: 3,
            }}
          >
            <BackToList sx={{ maxWidth: '140px' }} goBackOnly />
            <Box />
            <Box />
          </Box>

          {isLoading ? (
            <Box
              sx={{
                pb: '40px',
                height: '500px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '5px',
                backgroundColor: 'common.white',
              }}
            >
              <LoaderWrapper>
                <CircularProgress />
              </LoaderWrapper>
            </Box>
          ) : (
            <Box
              sx={{
                pb: '40px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '5px',
                backgroundColor: 'common.white',
              }}
            >
              <LockedPlanSummaryHeader
                statusInfo={mediaPlan?.data.statusInfo}
              />
              <PlanSummaryGeneralInfo />
              <TotalBudgetSummary countryForms={countryForms} />

              {/* Countries */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {countries.map((country) => {
                  const countryData = storedCountryForms?.[country];

                  const processedOfflineData =
                    countryData?.offline?.tv?.offlineBudget?.map(
                      (budget: any) => {
                        if (budget.touchpoint !== 'outdoor') return budget;
                        return {
                          ...budget,
                          children:
                            budget.children?.filter(
                              (child: any) => child.isIncludedInCalculations
                            ) || [],
                        };
                      }
                    ) ?? [];

                  const processedDigitalData =
                    countryData?.calculatedBudget?.filter(
                      (budget: any) => budget.isIncludedInCalculations
                    ) ?? [];

                  return (
                    <Accordion key={country}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <AccordionSummaryContent country={country} />
                      </AccordionSummary>

                      <AccordionDetails>
                        <CountryInfoTable country={country} />
                        <DigitalBudgetTable
                          data={groupCalculatedBudgetTouchpointData(
                            processedDigitalData
                          )}
                        />
                        <OfflineBudgetTable data={processedOfflineData} />
                        {mediaPlan?.data.countrySetup?.[country]?.ddx?.type && (
                          <DDXAcquisitionTable
                            ddx={mediaPlan?.data.countrySetup?.[country]?.ddx}
                            currency={
                              storedCountryForms[country]?.calculatedBudget?.[0]
                                .budget.currency
                            }
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
        <Grid
          container
          sx={{
            px: '24px',
            pt: '40px',
            borderTop: '1px solid',
            borderColor: 'border.main',
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              columnGap: '24px',
            }}
          >
            {id ? (
              <Button
                variant="text"
                sx={{ borderRadius: '30px', height: '34px' }}
                onClick={() => generateXls(id)}
                disabled={isLoading}
              >
                {t('mediaPlanning.downloadXls')}
              </Button>
            ) : null}

            {getAbilityForAccepting(userRoles) && (
              <Button
                onClick={handleClickUnlockPlan}
                variant="outlined"
                disabled={
                  !id || isUnlocking || isLoading || mediaPlan?.data.isLocked
                }
                sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LockOpenIcon />
                  <span>{t('mediaPlanning.unlockPlan')}</span>
                </Stack>
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      <AcceptPlanFlow
        open={acceptPlanOpen}
        onClose={handleCloseAcceptPlan}
        onSuccess={handleProceedAcceptUnlock}
        status={currentStatus}
      />
    </>
  );
}
